import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, AlertTriangle, FileSpreadsheet, History, Loader2, Save, UploadCloud } from 'lucide-react';
import { Modal } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { PageTitle } from '../../../../_metronic/layout/core';
import { ConfirmationModal } from '../default/ConfirmationModal';
import { EditableTable } from '../default/EditableTable';
import { Change, TableConfig } from '../default/types';
import { fetchMetasUl, saveMetasUl } from './api';
import { HistoryModal } from './HistoryModal';
import { ImportModal } from './ImportModal';
import { MetaUlData, MetaUlImportResponse } from './types';

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, index) => String(currentYear - 3 + index));

const EMPTY_DATA: MetaUlData = {
  year: currentYear,
  columns: [],
  rows: [],
  updated_at: null,
};

const MetasUnileverScreen: React.FC = () => {
  const [selectedYear, setSelectedYear] = useState(String(currentYear));
  const [tableData, setTableData] = useState<MetaUlData>(EMPTY_DATA);
  const [editedCells, setEditedCells] = useState<Map<string, string>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);

  const tableConfig = useMemo<TableConfig>(() => ({
    columns: tableData.columns,
  }), [tableData.columns]);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setFeedback(null);

    const response = await fetchMetasUl(selectedYear);
    if (response.success && response.data) {
      setTableData(response.data);
      setEditedCells(new Map());
    } else {
      setTableData({ ...EMPTY_DATA, year: Number(selectedYear) });
      setFeedback({
        type: 'danger',
        message: response.error || 'N\u00e3o foi poss\u00edvel carregar as Metas UL',
      });
    }

    setIsLoading(false);
  }, [selectedYear]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCellChange = useCallback((rowIndex: number, colIndex: number, value: string) => {
    if (colIndex < 1) return;

    const cellKey = `approved-${rowIndex}-${colIndex}`;
    const originalValue = String(tableData.rows[rowIndex]?.[colIndex] ?? '');

    setEditedCells((current) => {
      const next = new Map(current);
      if (String(value) === originalValue) {
        next.delete(cellKey);
      } else {
        next.set(cellKey, value);
      }
      return next;
    });
  }, [tableData.rows]);

  const getUpdatedRows = useCallback(() => (
    tableData.rows.map((row, rowIndex) => (
      row.map((cell, colIndex) => {
        const key = `approved-${rowIndex}-${colIndex}`;
        return editedCells.has(key) ? editedCells.get(key)! : String(cell ?? '');
      })
    ))
  ), [editedCells, tableData.rows]);

  const getChangeSummary = useCallback((): Change[] => {
    const changes: Change[] = [];

    editedCells.forEach((newValue, key) => {
      const [, rowIndexText, colIndexText] = key.split('-');
      const rowIndex = Number(rowIndexText);
      const colIndex = Number(colIndexText);
      const row = tableData.rows[rowIndex];
      const column = tableData.columns[colIndex];

      changes.push({
        cell: `APROVADO - ${row?.[0] || '-'} - ${column?.label || '-'}`,
        original: String(row?.[colIndex] ?? ''),
        new: newValue,
      });
    });

    return changes;
  }, [editedCells, tableData.columns, tableData.rows]);

  const handleSave = async () => {
    if (editedCells.size === 0) return;

    setIsSaving(true);
    setFeedback(null);

    const response = await saveMetasUl(selectedYear, getUpdatedRows());
    if (response.success && response.data) {
      setTableData(response.data);
      setEditedCells(new Map());
      setShowConfirmModal(false);
      setFeedback({
        type: 'success',
        message: `${response.changedCount || 0} ${response.changedCount === 1 ? 'valor atualizado' : 'valores atualizados'} com sucesso.`,
      });
    } else {
      setFeedback({
        type: 'danger',
        message: response.error || 'N\u00e3o foi poss\u00edvel salvar as Metas UL',
      });
    }

    setIsSaving(false);
  };

  const handleExport = () => {
    if (tableData.columns.length === 0) return;

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet([
      tableData.columns.map((column) => column.label),
      ...getUpdatedRows(),
    ]);

    worksheet['!cols'] = [
      { wch: 12 },
      ...Array.from({ length: 12 }, () => ({ wch: 16 })),
    ];

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Aprovado');
    XLSX.writeFile(workbook, `metas-ul-aprovado-${selectedYear}.xlsx`);
  };

  const handleImported = async (response: MetaUlImportResponse) => {
    setShowImportModal(false);
    await loadData();
    const changedCount = response.changedCount || 0;
    const importedCount = response.importedCount || 0;
    const years = response.years?.join(', ');
    setFeedback({
      type: 'success',
      message: `${importedCount} registros processados e ${changedCount} ${changedCount === 1 ? 'valor alterado' : 'valores alterados'}${years ? ` nos anos ${years}` : ''}.`,
    });
  };

  return (
    <>
      <PageTitle>Data Transfer - Metas UL</PageTitle>

      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto px-2 sm:px-3 lg:px-4">
          <div className="mb-8">
            <div className="flex flex-col justify-between gap-4 xl:flex-row xl:items-center">
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-gray-900">Metas UL</h1>
                <p className="mt-1 text-sm text-gray-500">Metas aprovadas da Ind&uacute;stria Unilever por BU e m&ecirc;s.</p>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <select
                  value={selectedYear}
                  onChange={(event) => setSelectedYear(event.target.value)}
                  disabled={isLoading || isSaving}
                  className="rounded-lg border border-gray-200 bg-white px-4 py-2 focus:ring-2 focus:ring-blue-500"
                >
                  {YEAR_OPTIONS.map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>

                <button
                  type="button"
                  onClick={() => setShowHistoryModal(true)}
                  className="inline-flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-200"
                >
                  <History className="h-5 w-5" />
                  Hist&oacute;rico
                </button>

                <button
                  type="button"
                  onClick={() => setShowImportModal(true)}
                  disabled={isLoading || isSaving || editedCells.size > 0}
                  title={editedCells.size > 0 ? 'Salve ou descarte as altera\u00e7\u00f5es manuais antes de importar' : 'Importar planilha de Metas UL'}
                  className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-blue-700 transition-colors hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <UploadCloud className="h-5 w-5" />
                  Importar dados
                </button>

                <button
                  type="button"
                  onClick={handleExport}
                  disabled={isLoading || tableData.rows.length === 0}
                  className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FileSpreadsheet className="h-5 w-5" />
                  Exportar Excel
                </button>

                <button
                  type="button"
                  onClick={() => setShowConfirmModal(true)}
                  disabled={editedCells.size === 0 || isSaving}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSaving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
                  {isSaving ? 'Salvando...' : 'Salvar Altera\u00e7\u00f5es'}
                </button>
              </div>
            </div>

            {editedCells.size > 0 ? (
              <div className="mt-4 flex items-center gap-2 rounded-xl border border-yellow-200 bg-yellow-50 p-4 text-sm font-medium text-yellow-800">
                <AlertTriangle className="h-5 w-5" />
                {editedCells.size} {editedCells.size === 1 ? 'c\u00e9lula alterada' : 'c\u00e9lulas alteradas'} aguardando salvamento.
              </div>
            ) : null}

            {feedback ? (
              <div className={`mt-4 flex items-center gap-2 rounded-xl border p-4 text-sm font-medium ${
                feedback.type === 'success'
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                  : 'border-red-200 bg-red-50 text-red-700'
              }`}>
                <AlertCircle className="h-5 w-5" />
                {feedback.message}
              </div>
            ) : null}
          </div>

          <div>
            <div className="rounded-t-xl bg-gradient-to-r from-green-600 to-green-700 px-6 py-3 text-white">
              <h2 className="text-lg font-semibold text-white">APROVADO</h2>
            </div>
            <div className="overflow-hidden rounded-b-xl border-x border-b border-gray-200 bg-white shadow-sm">
              {isLoading ? (
                <div className="flex items-center justify-center gap-3 py-20 text-gray-500">
                  <Loader2 className="h-7 w-7 animate-spin text-blue-600" />
                  Carregando metas...
                </div>
              ) : tableData.rows.length > 0 ? (
                <EditableTable
                  data={{ config: tableConfig, rows: tableData.rows }}
                  editedCells={editedCells}
                  onCellChange={handleCellChange}
                  tablePrefix="approved"
                  deferFormattingWhileEditing
                  compact
                />
              ) : (
                <div className="py-20 text-center text-sm text-gray-500">Nenhuma estrutura de metas dispon&iacute;vel.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <ConfirmationModal
          changes={getChangeSummary()}
          onConfirm={handleSave}
          onCancel={() => setShowConfirmModal(false)}
          isLoading={isSaving}
          config={tableConfig}
        />
      </Modal>

      <Modal
        show={showImportModal}
        onHide={() => setShowImportModal(false)}
        dialogClassName="modal-xl"
        backdropClassName="!bg-slate-950 !opacity-70 backdrop-blur-[2px]"
        contentClassName="overflow-visible border-0 bg-transparent shadow-none"
        backdrop="static"
        keyboard={false}
        centered
      >
        <ImportModal onClose={() => setShowImportModal(false)} onImported={handleImported} />
      </Modal>

      <Modal
        show={showHistoryModal}
        onHide={() => setShowHistoryModal(false)}
        dialogClassName="modal-xl"
        backdropClassName="!bg-slate-950 !opacity-70 backdrop-blur-[2px]"
        contentClassName="overflow-visible border-0 bg-transparent shadow-none"
        centered
      >
        <HistoryModal year={selectedYear} onClose={() => setShowHistoryModal(false)} />
      </Modal>
    </>
  );
};

export default MetasUnileverScreen;
